"use client";

import Image from "next/image";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import Check from "@/components/icons/Check";
import CTALink from "@/components/CTA/variants/CtaLink";
import { CTAProps } from "@/components/CTA/types";
import { validateLocation } from "@/utils/functions/validateLocation";

const DEFAULT_CHECKLIST_ITEMS = [
  "5-minute quiz",
  "Personalized results",
  "Easy enrollment",
];

export default function FindYourPath({
  title,
  subtitle,
  cta,
  brandimage,
  imagealttext,
  locationRestrictions,
  supportBulletFirst,
  supportBulletSecond,
  supportBulletThird,
}: CTAProps) {
  const isValidLocation = validateLocation(
    locationRestrictions?.allowedIn || [],
    locationRestrictions?.blockedIn || [],
  );

  if (!isValidLocation) return null;

  const checklistFromCms = [
    supportBulletFirst,
    supportBulletSecond,
    supportBulletThird,
  ]
    .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
    .filter((entry) => entry.length > 0);

  const checklistItems =
    checklistFromCms.length > 0
      ? checklistFromCms
      : [...DEFAULT_CHECKLIST_ITEMS];

  return (
    <section
      aria-labelledby="fyp-title"
      className="not-prose font-sans flex flex-col lg:flex-row bg-secondary-700 text-white p-4 lg:p-0 overflow-hidden"
    >
      <div className="flex flex-col gap-4 lg:w-[55%] lg:shrink-0 lg:pl-14 lg:my-auto lg:py-12">
        <Breadcrumbs
          path="find-your-path"
          variant="light"
          containerClassName="not-prose mb-0 [&_li[aria-hidden]_svg]:!text-putty-700 [&_li[aria-hidden]_svg]:![stroke:theme(colors.putty.700)]"
        />
        <div className="flex items-start lg:items-center gap-2">
          <Image
            src="/icons/ai-icon.png"
            alt=""
            width={36}
            height={36}
            className="m-0"
          />
          <h2 id="fyp-title" className="font-black italic text-white m-0">
            {title}
          </h2>
        </div>

        {subtitle && <p className="font-normal text-white m-0">{subtitle}</p>}

        {cta && (
          <div className="w-fit">
            <CTALink type="brand" label={cta.label} url={cta.url} />
          </div>
        )}

        <ul className="flex flex-wrap gap-2 sm:gap-6 list-none !p-0 !pl-0 m-0 text-white">
          {checklistItems.map((item, index) => (
            <li
              key={`fyp-bullet-${index}`}
              className="flex items-center gap-1 m-0"
            >
              <Check svgProps={{ width: "20", height: "20" }} />
              <span className="text-sm">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {brandimage && (
        <div className="hidden lg:block lg:flex-1 lg:relative lg:min-h-[300px] clip-slant-left">
          <Image
            src={brandimage}
            alt={imagealttext ?? ""}
            fill
            className="m-0 object-cover"
          />
        </div>
      )}
    </section>
  );
}
